import semverLt from 'semver/functions/lt';
import parse from 'semver/functions/parse';

import {
  CAN_POST_MESSAGE, GUID_NOT_RECEIVED, ALLOWED_ORIGINS,
} from '../consts';
import {
  MIN_API_VER, REC_API_VER, MIN_OS_VER, REC_OS_VER, UPC_VER,
} from '../env';
import i18n from '../i18n';

import validateUnraidVersion from '../helpers/validateUnraidVersion';

/**
 * @property actions: {Array} - ['extend', 'purchase', 'recover', 'replace', 'upgrade']
 * @property humanReadable {String}
 * @property heading {String}
 * @property message {String}
 * @property error {Boolean|Object} // see `ENOFLASH` below for error object example
 * @property error.heading {String}
 * @property error.message {String}
 * @property error.signInToFix {Boolean}
 */
const stateDataDefault = {
  actions: [],
  humanReadable: '',
  heading: '',
  message: '',
  messageCenter: false,
  error: false,
  withKey: false,
};

const ENOFLASH = {
  ...stateDataDefault,
  humanReadable: i18n.tc('stateData.ENOFLASH.humanReadable'),
  error: {
    heading: i18n.tc('stateData.ENOFLASH.error.heading'),
    message: i18n.tc('stateData.ENOFLASH.error.message'),
    signInToFix: false,
  },
};

let EGUID_MSG = '';

// eslint-disable-next-line import/prefer-default-export
export const getters = {
  statePhpPath: state => `/plugins/${state.plgPath || 'dynamix.unraid.net'}/include/state.php`,
  myServersCfgPath: state => `${state.plgPath || 'Unraid.net'}/myservers.cfg`,
  requireGuidValidation: (state) => {
    if (CAN_POST_MESSAGE) return state.guidRegistered;
    return true; // this means we're in the UPC and can pass guid validation
  },
  validApiKeyLength: state => state.apikey.length === 64,
  validApiKeyForUpc: state => (state.apikey ? state.apikey.startsWith('unupc_') : false),
  hasRemoteApikey: state => (state.hasRemoteApikey === true || !!(state.validApiKeyLength && state.validApiKeyForUpc)),
  showExpiretime: state => (state.state === 'TRIAL' || state.state === 'EEXPIRED') && state.expiretime > 0,
  trialExtensionEligible: state => (!!(!state.reggen || state.reggen < 2)), // used below to allow button actions
  signedOutWithKey: (state, self) => (!!(self.stateData.withKey && !state.registered)), // watched in Launchpad & UPC
  stateData: (state, self) => {
    switch (state.state) {
    case 'ENOKEYFILE': return {
      ...stateDataDefault,
      actions: [
        state.registered ? 'signOut' : 'register',
        'startTrial',
        'purchase',
        'redeem',
      ],
      humanReadable: i18n.tc('stateData.ENOKEYFILE.humanReadable'),
      heading: i18n.tc('stateData.ENOKEYFILE.heading'),
      message: i18n.tc('stateData.ENOKEYFILE.message'),
    };
    case 'TRIAL': return {
      ...stateDataDefault,
      actions: [
        'purchase',
        state.registered ? 'signOut' : 'register',
        state.registered ? 'continueTrial' : null,
      ],
      humanReadable: i18n.tc('stateData.TRIAL.humanReadable'),
      heading: i18n.tc('stateData.TRIAL.heading'),
      message: i18n.tc('stateData.TRIAL.message'),
      withKey: true,
    };
    case 'EEXPIRED': return {
      ...stateDataDefault,
      actions: [
        state.registered ? 'signOut' : 'register',
        // both below used to use `&& state.guidRegistered`
        self.trialExtensionEligible ? 'extend' : null,
        'purchase',
      ],
      humanReadable: i18n.tc('stateData.EEXPIRED.humanReadable'),
      error: {
        heading: i18n.tc('stateData.EEXPIRED.heading'),
        message: self.trialExtensionEligible
          ? i18n.tc('stateData.EEXPIRED.message.extensionEligible')
          : i18n.tc('stateData.EEXPIRED.message.extensionNotEligible'),
      },
      withKey: true,
    };
    case 'BASIC': return {
      ...stateDataDefault,
      actions: [
        state.registered ? 'signOut' : 'register',
        'upgrade',
      ],
      humanReadable: i18n.tc('stateData.BASIC.humanReadable'),
      heading: i18n.tc('basicPlusPro.heading'),
      // eslint-disable-next-line no-nested-ternary
      message: !state.registered
        ? i18n.tc('basicPlusPro.message.registered')
        : (state.guidRegistered)
          ? i18n.tc('basicPlusPro.message.upgradeEligible')
          : '',
      messageCenter: !state.registered,
      withKey: true,
    };
    case 'PLUS': return {
      ...stateDataDefault,
      actions: [
        state.registered ? 'signOut' : 'register',
        'upgrade',
      ],
      humanReadable: i18n.tc('stateData.PLUS.humanReadable'),
      heading: i18n.tc('basicPlusPro.heading'),
      // eslint-disable-next-line no-nested-ternary
      message: !state.registered
        ? i18n.tc('basicPlusPro.message.registered')
        : (state.guidRegistered)
          ? i18n.tc('basicPlusPro.message.upgradeEligible')
          : '',
      messageCenter: !state.registered,
      withKey: true,
    };
    case 'PRO': return {
      ...stateDataDefault,
      actions: [
        state.registered ? 'signOut' : 'register',
        state.registered ? 'close' : null,
      ],
      humanReadable: i18n.tc('stateData.PRO.humanReadable'),
      heading: i18n.tc('basicPlusPro.heading'),
      message: !state.registered
        ? i18n.tc('basicPlusPro.message.registered')
        : '',
      messageCenter: !state.registered,
      withKey: true,
    };
    case 'EGUID':
      if (state.replaceable) EGUID_MSG = i18n.tc('stateData.EGUID.error.message.replacementEligible');
      else if (state.replaceable === false && state.guidBlacklisted) EGUID_MSG = `${i18n.tc('stateData.EGUID.error.message.default')} <br class="mb-2">${i18n.tc('stateData.EGUID.error.message.blacklisted')}`;
      else if (state.replaceable === false && !state.guidBlacklisted) EGUID_MSG = `${i18n.tc('stateData.EGUID.error.message.default')} <br class="mb-2">${i18n.tc('stateData.EGUID.error.message.replacementIneligible')}`;
      else EGUID_MSG = i18n.tc('stateData.EGUID.error.message.replacementEligible'); // basically state.replaceable === null
      return {
        ...stateDataDefault,
        actions: [
          'purchase',
          'replace', // we always show replace key button until we rightfully know we shouldn't
          state.registered ? 'signOut' : 'register',
        ],
        humanReadable: i18n.tc('stateData.EGUID.humanReadable'),
        error: {
          heading: i18n.tc('stateData.EGUID.error.heading'),
          // eslint-disable-next-line no-nested-ternary
          message: EGUID_MSG,
          signInToFix: true,
        },
      };
    case 'EGUID1': return {
      ...stateDataDefault,
      actions: [
        'purchase',
        state.registered ? 'signOut' : 'register',
      ],
      humanReadable: i18n.tc('stateData.EGUID1.humanReadable'),
      error: {
        heading: i18n.tc('stateData.EGUID1.error.heading'),
        message: i18n.tc('stateData.EGUID1.error.message'),
        signInToFix: true,
      },
    };
    case 'ENOKEYFILE2': return {
      ...stateDataDefault,
      actions: [
        'purchase',
        state.registered ? 'signOut' : 'register',
        state.registered ? 'recover' : null, // recovery requires that there's an account associated with the guid
      ],
      humanReadable: i18n.tc('stateData.ENOKEYFILE2.humanReadable'),
      error: {
        heading: i18n.tc('stateData.ENOKEYFILE2.error.heading'),
        message: i18n.tc('stateData.ENOKEYFILE2.error.message'),
        signInToFix: true,
      },
    };
    case 'ETRIAL': return {
      ...stateDataDefault,
      actions: [
        state.registered ? 'signOut' : 'register',
        'purchase',
      ],
      humanReadable: i18n.tc('stateData.ETRIAL.humanReadable'),
      error: {
        heading: i18n.tc('stateData.ETRIAL.error.heading'),
        message: i18n.tc('stateData.ETRIAL.error.message'),
        signInToFix: true,
      },
    };
      /**
       * Begin states without sign in / key actions
       */
    case 'ENOKEYFILE1': return {
      ...stateDataDefault,
      humanReadable: i18n.tc('stateData.ENOKEYFILE1.humanReadable'),
      error: {
        heading: i18n.tc('stateData.ENOKEYFILE1.error.heading'),
        message: i18n.tc('stateData.ENOKEYFILE1.error.message'),
        signInToFix: false,
      },
    };
    case 'ENOFLASH': return ENOFLASH;
    case 'ENOFLASH1': return ENOFLASH;
    case 'ENOFLASH2': return ENOFLASH;
    case 'ENOFLASH3': return ENOFLASH;
    case 'ENOFLASH4': return ENOFLASH;
    case 'ENOFLASH5': return ENOFLASH;
    case 'ENOFLASH6': return ENOFLASH;
    case 'ENOFLASH7': return ENOFLASH;
    case 'EBLACKLISTED': return {
      ...stateDataDefault,
      humanReadable: i18n.tc('stateData.EBLACKLISTED.humanReadable'),
      error: {
        heading: i18n.tc('stateData.EBLACKLISTED.error.heading'),
        message: i18n.tc('stateData.EBLACKLISTED.error.message'),
        signInToFix: false,
      },
    };
    case 'EBLACKLISTED1': return {
      ...stateDataDefault,
      humanReadable: i18n.tc('stateData.EBLACKLISTED1.humanReadable'),
      error: {
        heading: i18n.tc('stateData.EBLACKLISTED1.error.heading'),
        message: i18n.tc('stateData.EBLACKLISTED1.error.message'),
        signInToFix: false,
      },
    };
    case 'EBLACKLISTED2': return {
      ...stateDataDefault,
      humanReadable: i18n.tc('stateData.EBLACKLISTED2.humanReadable'),
      error: {
        heading: i18n.tc('stateData.EBLACKLISTED2.error.heading'),
        message: i18n.tc('stateData.EBLACKLISTED2.error.message'),
        signInToFix: false,
      },
    };
    case 'ENOCONN': return {
      ...stateDataDefault,
      humanReadable: i18n.tc('stateData.ENOCONN.humanReadable'),
      error: {
        heading: i18n.tc('stateData.ENOCONN.error.heading'),
        message: i18n.tc('stateData.ENOCONN.error.message'),
        signInToFix: false,
      },
    };
    case 'STALE': return {
      ...stateDataDefault,
      humanReadable: i18n.tc('stateData.STALE.humanReadable'),
      error: {
        heading: i18n.tc('stateData.STALE.error.heading'),
        message: i18n.tc('stateData.STALE.error.message'),
        signInToFix: false,
      },
    };
    default: return { ...stateDataDefault };
    }
  },
  stateDataKeyActions: (_state, self) => !!(self.stateData.actions.includes('purchase')
    || self.stateData.actions.includes('upgrade')
    || self.stateData.actions.includes('recover')
    || self.stateData.actions.includes('replace')
    || self.stateData.actions.includes('extend')),
  keyTypeForPurchase: (state) => { /** @NOTE - DO NOT TRANSLATE THESE !!! These string values are used in the Craft checkout flow */
    switch (state.state) {
    case 'BASIC': return 'Basic';
    case 'PLUS': return 'Plus';
    case 'PRO': return 'Pro';
    default: return 'Trial';
    }
  },
  /**
   * Use NGINX_WANFQDN as a separate variable to help keep track between plg versions
   * if not present no remote access OR its the old version of PLG
   * if old version of PLG look for www.<HASH>.unraid.net,
   * */
  isRemoteAccess: state => state.wanFQDN || (state.site && state.site.includes('www.') && state.site.includes('unraid.net')),
  noRemoteApikeyRegisteredWithPlg: state => !!(state.registered && !state.hideMyServers && !!(state.validApiKeyLength && state.validApiKeyForUpc)),
  errorTooManyDisks: state => !!((state.config && !state.config.valid) && state.config.error === 'INVALID'), // if config.valid=false. then check for enum value of 'INVALID'
  allowedOrigins: state => [
    ...ALLOWED_ORIGINS,
    ...(state.extraOrigins),
  ],
  myServersNotMinApiVer: state => !state.apiVersion || (state.apiVersion && semverLt(state.apiVersion, MIN_API_VER)), // if we don't have apiVersion set to true as it's a very old version of the api
  myServersNotRecApiVer: state => !state.apiVersion || (state.apiVersion && semverLt(state.apiVersion, REC_API_VER)),
  osPrerelease: (state) => {
    if (state.osVersion) {
      const parsed = parse(state.osVersion);
      return parsed.prerelease.length;
    }
    return false;
  },
  osVersionValid: state => state.osVersion && validateUnraidVersion(state.osVersion, MIN_OS_VER, REC_OS_VER),
  // must be a successful plg install to show API / PLG message
  allowApiOutOfDate: (state, self) => !self.plgInstallFailed && state.apiVersion && (self.myServersNotMinApiVer || self.myServersNotRecApiVer) && !state.devMode && !state.hideMyServers,
  /**
   * priority if there are multiple errors/warnings:
   * error about api/plugin
   * error about OS
   * warning about api/plugin
   * warning about OS
   */
  myServersOutOfDate: (state, self) => {
    // show as error since minimum api version is not met
    if (self.allowApiOutOfDate && self.myServersNotMinApiVer) {
      return {
        ...state.myServersOutOfDate,
        error: true,
        message: 'The Connect plugin (formly known as My Servers) is out-of-date and will not connect to Unraid\'s cloud services. Please update the plugin.',
        linkText: 'Update now',
      };
    }
    // show OS version error
    if (!self.osVersionValid && !self.osPrerelease) {
      return {
        enabled: true,
        error: true,
        heading: 'Unraid OS Out-of-date',
        message: `You are running an unsupported release of Unraid, please upgrade to the current version.${state.plgInstalled && !self.plgInstallFailed ? ' This version of Unraid will be blocked from Connect (formly known as My Servers) in the future.' : ''}`,
        link: ' https://forums.unraid.net/topic/128328-my-servers-dropping-support-for-older-versions-of-unraid/',
        linkSameTab: false,
        linkText: i18n.tc('learnMore'),
      };
    }
    // show as warning since recomended api version is not met
    if (self.allowApiOutOfDate && !self.myServersNotMinApiVer && self.myServersNotRecApiVer) return state.myServersOutOfDate;
    // show OS version warning for non-stable
    if (!self.osVersionValid && self.osPrerelease) {
      return {
        enabled: true,
        error: false,
        heading: 'Unraid OS Out-of-date',
        message: `RC releases are not meant to used long term, please update to a stable release of Unraid.${state.plgInstalled && !self.plgInstallFailed ? ' This version of Unraid will be blocked from Connect (formly known as My Servers) in the future.' : ''}`,
        link: ' https://forums.unraid.net/topic/128328-my-servers-dropping-support-for-older-versions-of-unraid/',
        linkSameTab: false,
        linkText: i18n.tc('learnMore'),
      };
    }
    return { enabled: false, error: false };
  },
  /**
   * If we show the button we want to pass the error to the pop-up to use in the troubleshooting form
   * exception: let's hide the "owner" reponse error message in the pop-up on the success page
   */
  errorMessage: (state, self) => {
    if (!self.stateData.error && state.myServersError && state.myServersErrorShowSupportBtn) return state.myServersError;
    if (!self.stateData.error && state.cloud && state.cloud.error && state.myServersErrorShowSupportBtn) return state.cloud.error;
    return null;
  },
  tsHumanReadable: (state) => {
    if (!state.ts) return null;
    const tsMilliseconds = state.ts * 1000;
    const dateObject = new Date(tsMilliseconds);
    return dateObject.toString();
  },
  server: (state, self) => ({
    guid: state.guid || GUID_NOT_RECEIVED,
    regGuid: state.regGuid,
    guidRegistered: state.guidRegistered || state.regWizTime,
    guidValid: state.guidValid,
    hasRemoteApikey: self.hasRemoteApikey,
    regWizTime: state.regWizTime,
    keyfile: state.keyfile,
    keyTypeForPurchase: self.keyTypeForPurchase,
    flashproduct: state.flashproduct,
    flashvendor: state.flashvendor,
    servername: state.servername,
    serverdesc: state.serverdesc,
    internalip: state.internalip,
    site: state.site,
    wanFQDN: state.wanFQDN,
    state: state.state,
    internalport: state.internalport,
    protocol: state.protocol,
    registered: state.registered,
    reggen: state.reggen,
    userEmail: state.userEmail,
    expiretime: state.expiretime,
    ts: state.ts,
    tsHumanReadable: self.tsHumanReadable,
    locale: state.locale,
    deviceCount: state.deviceCount,
    plgPath: state.plgPath,
    plgVersion: state.plgVersion,
    osVersion: state.osVersion,
    apiVersion: state.apiVersion,
    upcVersion: UPC_VER,
    extraOrigins: state.extraOrigins,
    errorMessage: self.errorMessage,
    apiReport: state.apiReport,
    osGui: state.osGui,
    username: state.username,
    avatar: state.avatar,
    hideMyServers: state.hideMyServers,
    flashBackupActivated: state.flashBackupActivated,
    accessToken: state.accessToken,
    idToken: state.idToken,
    refreshToken: state.refreshToken,
  }),
  // eslint-disable-next-line no-unused-vars
  features: state => [
    {
      // title: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.dynamicRemoteAccess.heading'),
      // copy: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.dynamicRemoteAccess.copy'),
      title: 'Dynamic Remote Access',
      copy: 'Toggle on/off server accessibility with dynamic remote access. Automatically turn on UPnP and open a random WAN port on your router at the click of a button and close off access in seconds.',
    },
    {
      // title: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.manageWithinConnect.heading'),
      // copy: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.manageWithinConnect.copy'),
      title: 'Manage Your Server Within Connect',
      copy: 'Servers equipped with a myunraid.net certificate can be managed directly from within the Connect web UI. Manage multiple servers from your phone, tablet, laptop, or PC in the same browser window.',
    },
    {
      // title: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.deepLinking.heading'),
      // copy: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.deepLinking.copy'),
      title: 'Deep Linking',
      copy: 'The Connect dashboard links to relevant sections of the webgui, allowing quick access to those settings and server sections.',
    },
    {
      // title: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.onlineFlashBackup.heading'),
      // copy: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.onlineFlashBackup.copy'),
      title: 'Online Flash Backup',
      copy: 'Never ever be left without a backup of your config. If you need to change flash drives, generate a backup from Connect and be up and running in minutes.',
    },
    {
      // title: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.realTimeMonitoring.heading'),
      // copy: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.realTimeMonitoring.copy'),
      title: 'Real-time Monitoring',
      copy: 'Get an overview of your server\'s state, storage space, apps and VMs status, and more.',
    },
    {
      // title: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.customizableDashboardTitles.heading'),
      // copy: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.customizableDashboardTitles.copy'),
      title: 'Customizable Dashboard Tiles',
      copy: 'Set custom server tiles how you like and automatically display your server\'s banner image on your Connect Dashboard.',
    },
    {
      // title: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.licenseManagement.heading'),
      // copy: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.licenseManagement.copy'),
      title: 'License Management',
      copy: 'Manage your license keys at any time via the My Keys section.',
    },
    {
      // title: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.plusMore.heading'),
      // copy: i18n.tc('regWizPopUp.routes.whatIsMyServers.features.plusMore.copy'),
      title: 'Plus more on the way',
      copy: 'All you need is an active internet connection, an Unraid.net account, and the Connect plugin. Get started by installing the plugin.',
    },
  ],
  plgInstallFailed: state => state.plgInstalled && state.plgInstalled.includes('_installFailed'),
};
